<template>
  <b-overlay :show="pageBusy" variant="transparent">
    <b-row class="match-height mb-2">
      <b-col
        v-for="card in summaryCards"
        :key="card.label"
        lg="3"
        md="6"
        cols="12"
        class="mb-1"
      >
        <b-card class="mb-0 h-100">
          <div class="text-muted">{{ card.label }}</div>
          <h3 class="mb-0 mt-50">{{ card.value }}</h3>
          <small v-if="card.hint" class="text-muted">{{ card.hint }}</small>
        </b-card>
      </b-col>
    </b-row>

    <b-card>
      <div class="row pt-0">
        <div class="col-12 pb-1">
          <span class="font-weight-bolder">Filters</span>
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Search</label>
          <input
            v-model="search"
            type="text"
            class="form-control"
            placeholder="Event, organization, or city"
            @keyup.enter="filterEvents()"
          >
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Organizer status</label>
          <v-select
            v-model="status"
            :reduce="option => option.value"
            :options="statusOptions"
            :searchable="false"
            :clearable="false"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Public visibility</label>
          <v-select
            v-model="visibility"
            :reduce="option => option.value"
            :options="visibilityOptions"
            :searchable="false"
            :clearable="false"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6 pt-2" @click="filterEvents()">
          <span class="btn btn-primary">
            <feather-icon icon="SearchIcon" /> Search
          </span>
        </div>
      </div>
    </b-card>

    <b-card>
      <b-overlay :show="itemBusy">
        <p v-if="loadError" class="text-danger mb-1">{{ loadError }}</p>
        <b-table
          class="rounded border"
          :items="items"
          :fields="fields"
          responsive
          show-empty
          empty-text="No ticket events match these filters."
        >
          <template #head()="scope">
            <div class="text-nowrap text-primary">{{ scope.label }}</div>
          </template>

          <template #cell(title)="data">
            <div v-if="data.item.id" class="pt-1">
              <div class="font-weight-bolder">{{ data.item.title }}</div>
              <div class="text-muted small">
                {{ data.item.isOnline ? 'Online' : (data.item.city || data.item.venueName || '—') }}
              </div>
            </div>
          </template>

          <template #cell(organization)="data">
            <div v-if="data.item.id" class="pt-1">
              <div>{{ data.item.organization.name }}</div>
              <div class="text-muted small">{{ data.item.organization.country }}</div>
            </div>
          </template>

          <template #cell(visibility)="data">
            <b-badge v-if="data.item.id" :variant="visibilityVariant(data.item.visibility)">
              {{ visibilityLabel(data.item.visibility) }}
            </b-badge>
          </template>

          <template #cell(status)="data">
            <span v-if="data.item.id" class="text-nowrap">{{ statusLabel(data.item.status) }}</span>
          </template>

          <template #cell(startsAt)="data">
            <span v-if="data.item.id" class="text-nowrap">
              {{ dayjs(data.item.startsAt).format('MMM D, YYYY h:mm A') }}
            </span>
          </template>

          <template #cell(sold)="data">
            <span v-if="data.item.id" class="text-nowrap font-weight-bolder">
              {{ data.item.sold }} <span class="text-muted font-weight-normal">/ {{ data.item.capacity }}</span>
            </span>
          </template>

          <template #cell(grossCents)="data">
            <span v-if="data.item.id" class="text-nowrap">
              {{ money(data.item.grossCents, data.item.currency) }}
            </span>
          </template>

          <template #cell(actions)="data">
            <b-button
              v-if="data.item.id"
              size="sm"
              variant="outline-primary"
              :to="{ name: 'tickets-event', params: { id: data.item.id } }"
            >
              Manage
            </b-button>
          </template>
        </b-table>
      </b-overlay>

      <div class="d-flex p-1">
        <div class="w-100">
          Page <span class="text-primary">{{ currentPage }}</span> of
          <span class="text-primary">{{ pageCount }}</span>
        </div>
        <div class="w-100">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="pageSize"
            align="right"
            size="sm"
            class="my-0"
            @page-click="loadNewPage"
          />
        </div>
      </div>
    </b-card>
  </b-overlay>
</template>

<script>
import vSelect from 'vue-select'
import dayjs from 'dayjs'
import API from './api'
import { money, VISIBILITY_LABELS, visibilityVariant } from './format'

export default {
  name: 'TicketEvents',
  components: { vSelect },
  data() {
    return {
      dayjs,
      money,
      visibilityVariant,
      currentPage: 1,
      totalRows: 0,
      pageSize: 10,
      search: '',
      status: '',
      visibility: '',
      statusOptions: [
        { label: 'All', value: '' },
        { label: 'Published', value: 'PUBLISHED' },
        { label: 'Draft', value: 'DRAFT' },
        { label: 'Cancelled', value: 'CANCELLED' },
      ],
      visibilityOptions: [
        { label: 'All', value: '' },
        { label: 'Public', value: 'public' },
        { label: 'Hidden by staff', value: 'hidden' },
        { label: 'Private link', value: 'private' },
        { label: 'Draft', value: 'draft' },
      ],
      items: [],
      overview: null,
      fields: [
        { key: 'title', label: 'Event' },
        { key: 'organization', label: 'Organization' },
        { key: 'visibility', label: 'Public' },
        { key: 'status', label: 'Status' },
        { key: 'startsAt', label: 'Starts' },
        { key: 'sold', label: 'Tickets sold' },
        { key: 'grossCents', label: 'Revenue' },
        { key: 'actions', label: '' },
      ],
      itemBusy: false,
      pageBusy: false,
      loadError: '',
    }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.totalRows / this.pageSize))
    },
    summaryCards() {
      const overview = this.overview || {}
      const revenue = (overview.byCurrency || [])
        .map(row => money(row.grossCents, row.currency))
        .join(' · ')
      return [
        { label: 'Events', value: overview.eventCount ?? '—', hint: `${overview.publishedCount ?? 0} public` },
        { label: 'Organizations', value: overview.organizationCount ?? '—' },
        { label: 'Tickets sold', value: overview.ticketsSold ?? '—', hint: revenue ? `Revenue ${revenue}` : `${overview.checkedIn ?? 0} checked in` },
        {
          label: 'Hidden by staff',
          value: overview.hiddenCount ?? '—',
          hint: 'Not visible to the public',
        },
      ]
    },
  },
  created() {
    if (this.$route.query.org) this.search = String(this.$route.query.org)
    if (this.$route.query.visibility) this.visibility = String(this.$route.query.visibility)
    this.getEvents(1)
  },
  methods: {
    visibilityLabel(value) {
      return VISIBILITY_LABELS[value] || value
    },
    statusLabel(value) {
      if (value === 'PUBLISHED') return 'Published'
      if (value === 'DRAFT') return 'Draft'
      if (value === 'CANCELLED') return 'Cancelled'
      return value
    },
    loadNewPage(event, page) {
      event.preventDefault()
      this.getEvents(page)
    },
    filterEvents() {
      this.getEvents(1)
    },
    getEvents(page = 1) {
      this.itemBusy = true
      this.loadError = ''
      API.listEvents({
        q: this.search || undefined,
        status: this.status || undefined,
        visibility: this.visibility || undefined,
        page,
        pageSize: this.pageSize,
      }).then(res => {
        this.itemBusy = false
        if (res && res.success && res.data) {
          this.currentPage = res.data.page || page
          this.totalRows = res.data.total || 0
          this.pageSize = res.data.pageSize || this.pageSize
          this.items = res.data.list || []
          this.overview = res.data.overview || this.overview
          return
        }
        this.items = []
        this.loadError = (res && res.errorMsg) || 'Could not load ticket events.'
      }).catch(() => {
        this.itemBusy = false
        this.loadError = 'Could not load ticket events.'
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
